import {type RefObject, useEffect} from "react";

/**
 * Помощник мягкой клавиатуры для форм (вход, регистрация, сброс пароля, код, обращение).
 *
 * Жалоба Ирины (Capacitor-APK, Android): после ввода email и пароля клавиатура Яндекса
 * не убирается и закрывает «Войти». Причины две: у клавиши действия нет понятного
 * поведения (Enter ничего не «завершает»), а по тапу мимо поля фокус остаётся в поле.
 * Хук навешивается на контейнер формы ОДИН раз и даёт:
 *
 *  1. enterKeyHint: «далее» на всех полях, кроме последнего, «Войти/go» — на последнем
 *     (меняет подпись и вид клавиши действия).
 *  2. Enter в поле (кроме textarea): не последнее — фокус на следующее; последнее —
 *     отправка формы и blur, то есть клавиатура убирается.
 *  3. Тап по неинтерактивной части контейнера снимает фокус с поля. Элементы, которым
 *     клавиатура нужна открытой (например «глаз» пароля), помечаются data-keep-keyboard.
 *  4. Пока клавиатура открыта (visualViewport ужался), нижний отступ контейнера
 *     (--rla-kb-inset) даёт прокрутить кнопку выше клавиатуры, а сфокусированное поле
 *     подкручивается в видимую область. Если WebView сам уменьшает высоту
 *     (adjustResize), inset = 0 и поведение не меняется. На десктопе visualViewport
 *     не сжимается, так что ничего не происходит.
 */

const TEXT_FIELD_SELECTOR =
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"])' +
    ':not([type="button"]):not([type="submit"]):not([disabled]):not([readonly]), select:not([disabled])';
const KEEP_KEYBOARD_SELECTOR =
    'input, textarea, select, label, [contenteditable="true"], [data-keep-keyboard]';
const KEYBOARD_MIN_SHRINK_PX = 120;

function isTextInput(el: EventTarget | null): el is HTMLInputElement {
    return el instanceof HTMLInputElement && el.matches(TEXT_FIELD_SELECTOR);
}

export function useSoftKeyboardAssist(containerRef: RefObject<HTMLElement | null>, enabled = true): void {
    useEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container) return;

        const fieldsOf = (input: HTMLInputElement): HTMLInputElement[] => {
            const scope = input.form ?? container;
            return Array.from(scope.querySelectorAll<HTMLInputElement>(TEXT_FIELD_SELECTOR)).filter(
                (el) => el instanceof HTMLInputElement && el.offsetParent !== null,
            );
        };

        const scrollTimers = new Set<number>();
        const scrollFocusedIntoView = () => {
            const active = document.activeElement;
            if (!(active instanceof HTMLElement) || !container.contains(active)) return;
            active.scrollIntoView({block: "center", behavior: "auto"});
        };
        const scheduleScroll = () => {
            // Клавиатура выезжает ~250 мс: прокручиваем после того, как viewport устоялся.
            const id = window.setTimeout(() => {
                scrollTimers.delete(id);
                scrollFocusedIntoView();
            }, 280);
            scrollTimers.add(id);
        };

        const onFocusIn = (event: FocusEvent) => {
            const target = event.target;
            if (isTextInput(target)) {
                if (!target.hasAttribute("enterkeyhint")) {
                    const fields = fieldsOf(target);
                    const isLast = fields[fields.length - 1] === target;
                    target.enterKeyHint = isLast ? "go" : "next";
                }
            }
            if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) scheduleScroll();
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Enter" || event.isComposing || event.shiftKey) return;
            const input = event.target;
            if (!isTextInput(input)) return;
            const fields = fieldsOf(input);
            const index = fields.indexOf(input);
            const next = fields[index + 1];
            if (next) {
                event.preventDefault();
                next.focus();
                return;
            }
            // Последнее поле: отправляем и убираем клавиатуру.
            const form = input.form;
            if (form) {
                event.preventDefault();
                const submit = form.querySelector<HTMLButtonElement | HTMLInputElement>('[type="submit"]');
                if (!submit?.disabled) form.requestSubmit();
            }
            input.blur();
        };

        const onClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            const active = document.activeElement;
            if (!(active instanceof HTMLElement) || !container.contains(active)) return;
            if (!active.matches("input, textarea, select")) return;
            if (target.closest(KEEP_KEYBOARD_SELECTOR)) return;
            active.blur();
        };

        const vv = window.visualViewport;
        const onViewportResize = () => {
            if (!vv) return;
            const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
            const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_MIN_SHRINK_PX;
            container.style.setProperty("--rla-kb-inset", keyboardOpen ? `${inset}px` : "0px");
            if (keyboardOpen) scheduleScroll();
        };

        container.addEventListener("focusin", onFocusIn);
        container.addEventListener("keydown", onKeyDown);
        container.addEventListener("click", onClick);
        vv?.addEventListener("resize", onViewportResize);
        return () => {
            container.removeEventListener("focusin", onFocusIn);
            container.removeEventListener("keydown", onKeyDown);
            container.removeEventListener("click", onClick);
            vv?.removeEventListener("resize", onViewportResize);
            scrollTimers.forEach((id) => window.clearTimeout(id));
            container.style.removeProperty("--rla-kb-inset");
        };
    }, [containerRef, enabled]);
}
